import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/lib/site";

const AuthCtx = createContext(null);

const TOKEN_KEY = "af_token";
const ROLE_KEY = "af_role";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [role, setRole] = useState(() => localStorage.getItem(ROLE_KEY));
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(Boolean(token));

  const persist = useCallback((tk, rl) => {
    if (tk) {
      localStorage.setItem(TOKEN_KEY, tk);
      localStorage.setItem(ROLE_KEY, rl);
    } else {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(ROLE_KEY);
    }
    setToken(tk);
    setRole(rl);
  }, []);

  const headers = useCallback(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const refreshProfile = useCallback(async () => {
    if (!token || !role) {
      setProfile(null);
      setLoading(false);
      return;
    }
    try {
      const path = role === "admin" ? "/auth/admin/me" : "/auth/partner/me";
      const { data } = await axios.get(`${API}${path}`, { headers: headers() });
      setProfile(data);
    } catch {
      persist(null, null);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  }, [token, role, headers, persist]);

  useEffect(() => {
    refreshProfile();
  }, [refreshProfile]);

  const loginPartner = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/partner/login`, { email, password });
    persist(data.token, "partner");
    setProfile(data.partner);
    return data.partner;
  };

  const registerPartner = async (payload) => {
    const { data } = await axios.post(`${API}/auth/partner/register`, payload);
    persist(data.token, "partner");
    setProfile(data.partner);
    return data.partner;
  };

  const loginAdmin = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/admin/login`, { email, password });
    persist(data.token, "admin");
    setProfile(data.admin);
    return data.admin;
  };

  const logout = () => {
    persist(null, null);
    setProfile(null);
  };

  const value = {
    token,
    role,
    profile,
    loading,
    isAuthed: Boolean(token && profile),
    headers,
    loginPartner,
    registerPartner,
    loginAdmin,
    logout,
    refreshProfile,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);

export function formatApiError(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}
